import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Download, Upload, ShieldAlert, History, RotateCcw, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";

const REASON = { logout: "Saat Logout", daily: "Harian Otomatis", manual: "Manual" };

function downloadJson(data, name) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}

export default function Backup() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [snaps, setSnaps] = useState([]);
  const loadSnaps = () => api.get("/backup/snapshots").then((r) => setSnaps(r.data)).catch(() => {});
  useEffect(() => { loadSnaps(); }, []);

  const snapshotNow = async () => {
    setBusy(true);
    try { await api.post("/backup/auto", { reason: "manual" }); toast.success("Snapshot tersimpan di server"); loadSnaps(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const downloadSnap = async (s) => {
    const { data } = await api.get(`/backup/snapshots/${s.id}`);
    downloadJson(data, `snapshot-${s.date}-${s.reason}.json`);
  };
  const restoreSnap = async (s) => {
    if (!window.confirm(`Restore dari snapshot ${fmtDateTime(s.created_at)}? Data saat ini akan DITIMPA.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/backup/snapshots/${s.id}/restore`); toast.success(`Restore selesai (${data.restored.length} koleksi)`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const clearSnaps = async () => {
    if (!window.confirm("Hapus SEMUA riwayat snapshot otomatis secara permanen? Tindakan ini tidak dapat dibatalkan.")) return;
    setBusy(true);
    try { const { data } = await api.delete("/backup/snapshots"); toast.success(`${data.deleted} snapshot dihapus`); loadSnaps(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const doBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/backup");
      downloadJson(data, `backup-sumberharapan-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Backup diunduh");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Restore akan MENIMPA seluruh data saat ini. Lanjutkan?")) { e.target.value = ""; return; }
    setBusy(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const { data: res } = await api.post("/restore", { data });
      toast.success(`Restore selesai (${res.restored.length} koleksi)`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "File tidak valid"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-5" data-testid="backup-page">
      <div>
        <h1 className="text-2xl font-bold font-display text-slate-900">Backup & Restore</h1>
        <p className="text-sm text-slate-500">Cadangkan atau pulihkan seluruh database & stok</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="h-12 w-12 rounded-xl bg-emerald-500 flex items-center justify-center mb-4"><Download className="h-6 w-6 text-white" /></div>
            <h3 className="font-semibold text-lg font-display">Backup Manual</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">Unduh salinan lengkap seluruh data (barang, transaksi, keuangan, user) dalam format JSON.</p>
            <Button onClick={doBackup} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="backup-database-button">
              <Download className="h-4 w-4 mr-1.5" /> Unduh Backup
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="h-12 w-12 rounded-xl bg-orange-500 flex items-center justify-center mb-4"><Upload className="h-6 w-6 text-white" /></div>
            <h3 className="font-semibold text-lg font-display">Restore Data</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">Pulihkan data dari file backup JSON. {user.role !== "master" && "(Hanya Master)"}</p>
            <input ref={fileRef} type="file" accept="application/json" onChange={doRestore} className="hidden" data-testid="restore-database-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy || user.role !== "master"} className="bg-orange-500 hover:bg-orange-600" data-testid="restore-trigger-button">
              <Upload className="h-4 w-4 mr-1.5" /> Pilih File & Restore
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Clock className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-800">
            <b>Auto-backup AKTIF:</b> snapshot database & stok dibuat otomatis di server saat Master/Admin logout dan setiap hari (tanpa klik manual). 14 snapshot terakhir disimpan dan dapat diunduh atau dipulihkan di bawah.
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h3 className="font-semibold font-display flex items-center gap-2"><History className="h-4 w-4 text-orange-500" /> Riwayat Snapshot Otomatis</h3>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={snapshotNow} disabled={busy} data-testid="snapshot-now-button">Buat Snapshot Sekarang</Button>
              {user.role === "master" && snaps.length > 0 && (
                <Button size="sm" variant="outline" onClick={clearSnaps} disabled={busy} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="clear-snapshots-button">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Hapus Riwayat
                </Button>
              )}
            </div>
          </div>
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Waktu</TableHead><TableHead>Pemicu</TableHead><TableHead>Oleh</TableHead><TableHead>Barang / Penjualan</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody data-testid="snapshot-list">
              {snaps.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-sm">{fmtDateTime(s.created_at)}</TableCell>
                  <TableCell><Badge variant="outline">{REASON[s.reason] || s.reason}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{s.by}</TableCell>
                  <TableCell className="text-sm">{s.counts?.items} / {s.counts?.sales}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => downloadSnap(s)} title="Unduh"><Download className="h-4 w-4" /></Button>
                    {user.role === "master" && <Button size="icon" variant="ghost" className="text-orange-600" onClick={() => restoreSnap(s)} title="Restore" disabled={busy}><RotateCcw className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
              {snaps.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Belum ada snapshot</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">Restore bersifat menimpa (overwrite) seluruh data — pastikan Anda memiliki cadangan terbaru sebelum memulihkan.</div>
        </CardContent>
      </Card>
    </div>
  );
}
